import React, { memo, useCallback, useEffect, useRef } from "react";
import { ImpressionContext } from ".";
import useData from "../../hooks/useData";
import { VIEWS } from "../../conf";

const IMPRESSION_THRESHOLD = 1;
const TICK_INTERVAL_IN_MS = 100;
const MAX_TIMELINE_RESET_THRESHOLD = 100;
const SNAPSHOT_TYPE = {
	ENTITY_IMPRESSION_SNAPSHOT: "ENTITY_IMPRESSION_SNAPSHOT",
};
export const generateEntityImpressionSnapshot = ({ id } = {}) => {
	if (!id) return;
	return {
		id,
		snapshot_time: Date.now(),
		snapshot_type: SNAPSHOT_TYPE.ENTITY_IMPRESSION_SNAPSHOT,
		snapshot_invalidated: false,
	};
};

const ImpressionManager = memo(({ children }) => {
	const { view } = useData();
	const activeEntities = useRef(new Set());
	const isEntityActive = useCallback(({ id } = {}) => {
		if (!id) return;
		return activeEntities.current.has(id);
	}, []);

	const entityCallback = useRef({});
	const addEntityCallback = useCallback((id, callback) => {
		entityCallback.current[id] = callback;
	}, []);
	const removeEntityCallback = useCallback((id) => {
		delete entityCallback.current[id];
	}, []);

	const entityTimeline = useRef([]);
	const targetTimelineProcIdx = useRef(0);

	const createEntityTimelineRecord = useCallback((record) => {
		if (
			!record ||
			record?.snapshot_type !== SNAPSHOT_TYPE.ENTITY_IMPRESSION_SNAPSHOT
		)
			return [false, null];

		entityTimeline.current.push(record);
		return [true, record];
	}, []);

	useEffect(() => {
		// Run the manager iff the view is widget (not overlay)

		// ADDING TEMP CONDITION FOR view === 'LEAD_GEN' to support old script cache
		if (view !== VIEWS.WIDGET && view !== "LEAD_GEN") return;

		const timelineProcessor = () => {
			const currTime = Date.now();

			const processRecord = (record, recordIdx) => {
				const invalidateSnapshot = () => {
					entityTimeline.current[
						recordIdx
					].snapshot_invalidated = true;
				};

				const { id, snapshot_time: snapshotTime } = record;

				if (!isEntityActive({ id })) {
					// Check validity at every tick
					// Invalidate snapshot if attached entity is inactive
					invalidateSnapshot();
				}

				if ((currTime - snapshotTime) / 1000 < IMPRESSION_THRESHOLD) {
					// Check if snapshot can be processed
					return false;
				}

				// Check validity of snapshot and process
				if (entityTimeline.current[recordIdx].snapshot_invalidated) {
					// Checks for both historic invalidity & current tick invalidity
					return true;
				}
				const callback = entityCallback.current?.[id];
				if (typeof callback === "function") {
					callback();
				} else {
					console.error("Callback not defined for id", id);
				}
				return true;
			};

			for (
				let i = targetTimelineProcIdx.current;
				i < entityTimeline.current.length;
				i++
			) {
				const targetRecord = entityTimeline.current[i];
				const wasRecordProcAttempted = processRecord(targetRecord, i);

				if (wasRecordProcAttempted) {
					targetTimelineProcIdx.current += 1;
				}
			}

			if (
				entityTimeline.current.length >= MAX_TIMELINE_RESET_THRESHOLD &&
				entityTimeline.current.length - targetTimelineProcIdx.current <
					MAX_TIMELINE_RESET_THRESHOLD
			) {
				// Trim the timeline to remove processed elements, reset idx tracker to 0
				entityTimeline.current = entityTimeline.current.slice(
					targetTimelineProcIdx.current
				);
				targetTimelineProcIdx.current = 0;
			}
		};
		const timelineProcessorOrchestrator = setInterval(
			timelineProcessor,
			TICK_INTERVAL_IN_MS
		);
		return () => {
			clearInterval(timelineProcessorOrchestrator);
		};
	}, [isEntityActive, view]);

	return (
		<ImpressionContext.Provider
			value={{
				activeEntities,
				isEntityActive,
				createEntityTimelineRecord,
				addEntityCallback,
				removeEntityCallback,
			}}
		>
			{children}
		</ImpressionContext.Provider>
	);
});

export default ImpressionManager;
